<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div
        v-if="listError"
        class="alert alert-danger d-flex align-items-center gap-2 mb-3"
      >
        <i class="bi bi-exclamation-triangle-fill" />
        <span>{{ listError }}</span>
      </div>
      <div
        v-if="listLoading"
        class="d-flex align-items-center gap-3 py-3"
      >
        <span
          class="spinner-border text-primary"
          role="status"
        />
        <span>{{ $t('components.assetGroupsList.loading') }}</span>
      </div>

      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-diagram-2" />
            </div>
            <div class="assets-grid-title__text">
              {{ $t('aside.menu.pages.assetsGroupsList') }}
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="refreshGrid"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <DxDataGrid
            ref="gridRef"
            :data-source="dataSource"
            :remote-operations="true"
            :hover-state-enabled="true"
            :column-auto-width="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :column-hiding-enabled="true"
            :word-wrap-enabled="true"
          >
            <DxHeaderFilter :visible="true" />
            <DxFilterRow :visible="true" />
            <DxPaging :page-size="20" />
            <DxPager
              :allowed-page-sizes="[20]"
              :show-info="true"
              :show-page-size-selector="false"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="name"
              :caption="$t('components.assetGroupsList.columns.name')"
              :min-width="220"
            />
            <DxColumn
              :caption="$t('components.assetGroupsList.columns.activity')"
              :min-width="200"
              cell-template="activity-template"
              :allow-filtering="false"
              :allow-sorting="false"
            />
            <DxColumn
              :caption="$t('components.assetGroupsList.columns.averageValue')"
              :min-width="240"
              cell-template="average-template"
              :allow-filtering="false"
              :allow-sorting="false"
            />
            <DxColumn
              :caption="$t('components.assetGroupsList.columns.actions')"
              :width="140"
              :allow-filtering="false"
              :allow-sorting="false"
              cell-template="actions-template"
            />

            <template #average-template="{ data }">
              <div class="average-cell">
                <div
                  class="param-chip"
                  v-if="data.data.temperatureAvg !== null && data.data.temperatureAvg !== undefined"
                >
                  <i class="bi bi-thermometer-half" />
                  <span>{{ formatAverage(data.data.temperatureAvg, '°C') }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
                <div
                  class="param-chip"
                  v-if="data.data.humidityAvg !== null && data.data.humidityAvg !== undefined"
                >
                  <i class="bi bi-droplet-half" />
                  <span>{{ formatAverage(data.data.humidityAvg, '%') }}</span>
                </div>
                <div
                  class="param-chip param-chip--empty"
                  v-else
                >
                  —
                </div>
              </div>
            </template>

            <template #activity-template="{ data }">
              <div class="status-cell">
                <div class="status-line">
                  <span class="param-chip status-chip status-chip--recent">
                    {{ data.data.activeRecentCount ?? 0 }} {{ $t('components.assetGroupsList.status.activeRecent') }}
                  </span>
                  <span class="status-hint">{{ $t('components.assetGroupsList.status.activeRecentHint') }}</span>
                </div>
                <div class="status-line">
                  <span class="param-chip status-chip status-chip--active">
                    {{ data.data.activeDailyCount ?? 0 }} {{ $t('components.assetGroupsList.status.activeDaily') }}
                  </span>
                  <span class="status-hint">{{ $t('components.assetGroupsList.status.activeDailyHint') }}</span>
                </div>
                <div class="status-line">
                  <span class="param-chip status-chip status-chip--inactive">
                    {{ data.data.inactiveCount ?? 0 }} {{ $t('components.assetGroupsList.status.inactive') }}
                  </span>
                  <span class="status-hint">{{ $t('components.assetGroupsList.status.inactiveHint') }}</span>
                </div>
              </div>
            </template>

            <template #actions-template="{ data }">
              <div class="action-buttons">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :title="$t('components.assetGroupsList.actions.details')"
                  @click="goToDetails(data.data)"
                >
                  <i class="bi bi-eye" />
                </button>
              </div>
            </template>
          </DxDataGrid>
          <div
            v-if="isGridEmpty"
            class="text-center text-muted py-3"
          >
            {{ $t('components.assetGroupsList.empty') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { getAssetGroupsAverages } from '@/crud/asset-group'

type AverageRow = {
  id: number
  name: string
  temperatureAvg?: number | null
  humidityAvg?: number | null
  activeRecentCount?: number | null
  activeDailyCount?: number | null
  inactiveCount?: number | null
}

export default defineComponent({
  name: 'AssetGroupsAveragesView',
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager
  },
  emits: ['pageName'],
  setup(props, context) {
    const { t, locale } = useI18n()
    const router = useRouter()
    const gridRef = ref()
    const listLoading = ref(false)
    const listError = ref<string | null>(null)

    const isGridEmpty = computed(() => {
      const inst = gridRef.value?.instance
      return !listLoading.value && !listError.value && inst && inst.totalCount() === 0
    })

    const setTitle = () => context.emit('pageName', t('aside.menu.pages.assetsGroupsList'))
    setTitle()
    watch(locale, setTitle)

    const buildSearchValue = (filter: any): string => {
      if (!filter) return ''

      const extract = (f: any): string => {
        if (Array.isArray(f) && f.length >= 3 && typeof f[0] === 'string' && f[0] === 'name') {
          return f[2] || ''
        }
        return ''
      }

      const gather = (f: any): string => {
        if (!f) return ''
        if (typeof f[0] === 'string') {
          return extract(f)
        }
        if (Array.isArray(f)) {
          for (const item of f) {
            const val = gather(item)
            if (val) return val
          }
        }
        return ''
      }

      return gather(filter)
    }

    const dataSource = new CustomStore({
      cacheRawData: false,
      key: 'id',
      load: async (loadOptions) => {
        const params: any = {
          skip: loadOptions.skip ?? 0,
          take: loadOptions.take ?? 20
        }

        const searchValue = buildSearchValue(loadOptions.filter)
        if (searchValue) params.searchValue = searchValue

        if (loadOptions.sort) {
          params.sort = JSON.stringify(loadOptions.sort)
        }
        if (loadOptions.filter) {
          params.filter = JSON.stringify(loadOptions.filter)
        }

        try {
          listLoading.value = true
          listError.value = null

          const response = await getAssetGroupsAverages(params)
          const payload = response.data?.data || {}
          const rawRows: any[] = Array.isArray(payload) ? payload : (payload.result || payload.data || [])

          const rows: AverageRow[] = (rawRows || []).map((g: any) => ({
            id: g.id,
            name: g.name,
            temperatureAvg: g.temperatureAvg ?? null,
            humidityAvg: g.humidityAvg ?? null,
            activeRecentCount: g.activeRecentCount ?? 0,
            activeDailyCount: g.activeDailyCount ?? 0,
            inactiveCount: g.inactiveCount ?? 0
          }))

          const total = response.data?.totalCount ?? payload.count ?? 0
          return { data: rows, totalCount: total }
        } catch (e: any) {
          listError.value = e?.response?.data?.error || e?.message || t('components.assetGroupsList.error')
          throw e
        } finally {
          listLoading.value = false
        }
      }
    })

    const formatAverage = (value: number | null | undefined, unit: string): string => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return '-'
      }
      const num = typeof value === 'number' ? value : parseFloat(String(value))
      if (Number.isNaN(num)) {
        return '-'
      }
      return `${num.toFixed(2)} ${unit}`
    }

    const goToDetails = (row: AverageRow) => {
      if (!row?.id) return
      router.push({ name: 'AssetGroupDetails', params: { id: row.id } })
    }

    const refreshGrid = () => {
      gridRef.value?.instance?.refresh()
    }

    return {
      gridRef,
      listLoading,
      listError,
      dataSource,
      isGridEmpty,
      formatAverage,
      goToDetails,
      refreshGrid
    }
  }
})
</script>

<style src="@/assets/scss/asset-view-style.scss" lang="scss"></style>
<style scoped lang="scss">
@import '@/assets/scss/asset-groups-view.scss';
</style>
